import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import api from '../../services/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Robot, PaperPlaneTilt, Clock, Sparkle } from '@phosphor-icons/react';

const MarkdownMessage = ({ content }) => (
  <ReactMarkdown
    components={{
      p: ({ children }) => <p className="mb-2 last:mb-0 leading-relaxed">{children}</p>,
      strong: ({ children }) => <strong className="font-semibold text-white">{children}</strong>,
      em: ({ children }) => <em className="italic text-indigo-300">{children}</em>,
      ul: ({ children }) => <ul className="list-disc pl-4 mb-2 space-y-1">{children}</ul>,
      ol: ({ children }) => <ol className="list-decimal pl-4 mb-2 space-y-1">{children}</ol>,
      li: ({ children }) => <li className="leading-relaxed">{children}</li>,
      code: ({ inline, children }) =>
        inline
          ? <code className="px-1.5 py-0.5 rounded text-xs font-mono" style={{ background: 'rgba(99,102,241,0.15)', color: '#A5B4FC' }}>{children}</code>
          : <pre className="p-3 rounded-lg my-2 overflow-x-auto text-xs font-mono" style={{ background: 'rgba(15,23,42,0.6)', border: '1px solid rgba(148,163,184,0.12)' }}><code style={{ color: '#CBD5E1' }}>{children}</code></pre>,
      h1: ({ children }) => <h1 className="text-lg font-bold text-white mb-2">{children}</h1>,
      h2: ({ children }) => <h2 className="text-base font-bold text-white mb-1.5">{children}</h2>,
      h3: ({ children }) => <h3 className="text-sm font-bold text-white mb-1">{children}</h3>,
      a: ({ href, children }) => <a href={href} className="text-indigo-400 hover:underline" target="_blank" rel="noopener noreferrer">{children}</a>,
      blockquote: ({ children }) => <blockquote className="border-l-2 border-indigo-500 pl-3 my-2 italic" style={{ color: '#94A3B8' }}>{children}</blockquote>,
    }}
  >
    {content}
  </ReactMarkdown>
);

const AIAssistantPage = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `session-${Date.now()}`);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    api.getAiChatHistory().then(setHistory).catch(() => {});
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;
    const question = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: question, time: new Date().toISOString() }]);
    setLoading(true);
    try {
      const res = await api.aiLabAssistant(question, sessionId);
      setMessages(prev => [...prev, { role: 'assistant', text: res.response, time: new Date().toISOString() }]);
    } catch (e) {
      toast.error('AI assistant error');
      setMessages(prev => [...prev, { role: 'assistant', text: 'Sorry, I encountered an error. Please try again.', time: new Date().toISOString() }]);
    }
    setLoading(false);
    inputRef.current?.focus();
  };

  const suggestions = [
    "Show me a summary of recent experiments",
    "What inventory items are running low?",
    "Any open failure logs I should address?",
    "Suggest a protocol for Gibson Assembly",
    "What's the status of our current projects?",
  ];

  return (
    <div data-testid="ai-assistant-page" className="flex flex-col" style={{ height: 'calc(100vh - 120px)' }}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="heading-display flex items-center gap-2"><Sparkle size={24} className="text-indigo-400" /> AI Lab Assistant</h1>
          <p style={{ color: '#94A3B8' }} className="mt-1 text-sm">Ask anything about your lab data, protocols, and experiments</p>
        </div>
        <Button variant="outline" className="btn-secondary text-xs" onClick={() => setShowHistory(!showHistory)}>
          <Clock size={14} className="mr-1" /> History
        </Button>
      </div>

      <div className="flex-1 flex gap-4 min-h-0">
        {/* Chat Area */}
        <div className="flex-1 flex flex-col card-flat overflow-hidden">
          <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="chat-messages">
            {messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4" style={{ background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.15)' }}>
                  <Robot size={32} style={{ color: '#818CF8' }} />
                </div>
                <h3 className="text-lg font-semibold mb-2" style={{ color: '#F1F5F9' }}>How can I help?</h3>
                <p className="text-sm mb-6 max-w-md" style={{ color: '#94A3B8' }}>I have access to your lab's experiments, protocols, inventory, and failure logs.</p>
                <div className="flex flex-wrap gap-2 justify-center max-w-lg">
                  {suggestions.map((s, i) => (
                    <button
                      key={i}
                      className="text-xs px-3 py-1.5 rounded-full transition-colors"
                      style={{ border: '1px solid rgba(99,102,241,0.2)', color: '#A5B4FC', background: 'rgba(99,102,241,0.06)' }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'rgba(99,102,241,0.12)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'rgba(99,102,241,0.06)'; }}
                      onClick={() => { setInput(s); inputRef.current?.focus(); }}
                      data-testid={`suggestion-${i}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            ) : messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                {msg.role === 'assistant' && (
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mr-2 mt-1" style={{ background: 'rgba(99,102,241,0.12)' }}>
                    <Sparkle size={14} style={{ color: '#818CF8' }} />
                  </div>
                )}
                <div
                  className={`max-w-[80%] px-4 py-3 text-sm ${
                    msg.role === 'user'
                      ? 'rounded-2xl rounded-tr-md'
                      : 'rounded-2xl rounded-tl-md'
                  }`}
                  style={msg.role === 'user'
                    ? { background: 'linear-gradient(135deg, #6366F1, #4F46E5)', color: '#FFFFFF' }
                    : { background: '#1E293B', border: '1px solid rgba(148,163,184,0.12)', color: '#CBD5E1' }
                  }
                  data-testid={`message-${i}`}
                >
                  {msg.role === 'assistant' ? (
                    <MarkdownMessage content={msg.text} />
                  ) : (
                    <div className="whitespace-pre-wrap">{msg.text}</div>
                  )}
                  <p className="text-[10px] mt-2 opacity-50">
                    {new Date(msg.time).toLocaleTimeString()}
                  </p>
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mr-2 mt-1" style={{ background: 'rgba(99,102,241,0.12)' }}>
                  <Sparkle size={14} style={{ color: '#818CF8' }} />
                </div>
                <div className="rounded-2xl rounded-tl-md px-4 py-3" style={{ background: '#1E293B', border: '1px solid rgba(148,163,184,0.12)' }}>
                  <div className="flex gap-1.5">
                    <div className="w-2 h-2 rounded-full animate-bounce" style={{ background: '#818CF8', animationDelay: '0ms' }} />
                    <div className="w-2 h-2 rounded-full animate-bounce" style={{ background: '#818CF8', animationDelay: '150ms' }} />
                    <div className="w-2 h-2 rounded-full animate-bounce" style={{ background: '#818CF8', animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <form onSubmit={sendMessage} className="p-3 flex gap-2" style={{ borderTop: '1px solid rgba(148,163,184,0.1)' }} data-testid="chat-input-form">
            <input
              ref={inputRef}
              className="input-field flex-1"
              placeholder="Ask about experiments, protocols, inventory..."
              value={input}
              onChange={e => setInput(e.target.value)}
              disabled={loading}
              data-testid="chat-input"
            />
            <Button type="submit" className="btn-primary" disabled={loading || !input.trim()} data-testid="send-btn">
              <PaperPlaneTilt size={18} />
            </Button>
          </form>
        </div>

        {/* History Sidebar */}
        {showHistory && (
          <div className="w-72 card-flat p-3 overflow-y-auto">
            <h3 className="heading-card mb-3">Recent Conversations</h3>
            <div className="space-y-1">
              {history.length === 0 ? (
                <p className="text-xs" style={{ color: '#94A3B8' }}>No history yet</p>
              ) : history.slice(0, 20).map((h, i) => (
                <div key={i} className="p-2 rounded-lg cursor-pointer transition-colors" style={{ color: '#CBD5E1' }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(99,102,241,0.06)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                  onClick={() => setInput(h.question)}
                >
                  <p className="text-xs font-medium truncate">{h.question}</p>
                  <p className="text-[10px] mt-0.5" style={{ color: '#64748B' }}>{new Date(h.created_at).toLocaleDateString()}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIAssistantPage;
